/**
 * `/tenants` — every workspace Wend has provisioned.
 *
 * A directory, not a work surface. Its job is to let a staff member find one
 * workspace — by name, by contact address, or by spotting the one whose
 * subscription has lapsed — and open it. Everything that changes a workspace
 * lives on the detail screen behind the name, one per row.
 *
 * SUPERSEDED, and kept because the reasoning still applies to this screen: this
 * used to say it was read-only because editing a subscription, topping up
 * credits and flipping a feature flag were slice 8. Slice 8 built them, and they
 * went onto `/tenants/:tenantId` rather than into this table. A list that can
 * also edit has to answer "which row did I just change and what did it say
 * before" in a cell four columns wide, and it has nowhere to put the required
 * reason — which is the field that makes an admin write attributable at all.
 *
 * The subscription rule itself moved to `lib/tenant-status.ts`: this screen and
 * the detail screen must never disagree about whether a workspace can sign in.
 *
 * TWO SHAPES IN ONE ROW, ON PURPOSE (slice 66). The status is `@wend/ui`'s
 * Badge — the rounded, bordered capsule every status in the product wears — and
 * the features beside it are `FeatureTags`, square and borderless. They used to
 * be the same local `Pill` in two tones, so a row of four feature capsules sat
 * beside the one capsule that says whether anybody can sign in, and only its
 * colour told them apart. The column heads use the product's kicker type, as
 * every list header in the tenant app does.
 */
import { Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { DEFAULT_PAGE_SIZE } from '@wend/shared';
import { Badge, Button, Card, Input, KICKER_CLASS, Spinner, buttonVariants, cn } from '@wend/ui';

import { AdminShell } from '../components/AdminShell';
import { ErrorState } from '../components/ErrorState';
import { FeatureTags } from '../components/FeatureTags';
import { formatDate, formatRange, formatRelative } from '../lib/format';
import { subscriptionState } from '../lib/tenant-status';
import { listTenants } from '../lib/tenants-api';
import { useAsyncData } from '../lib/use-async-data';

/** Typing pauses this long before the list re-queries. */
const SEARCH_DEBOUNCE_MS = 300;

const HEAD_CELL = cn('px-4 py-3 text-left text-muted', KICKER_CLASS);

export function TenantsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) =>
      listTenants(
        {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          ...(search === '' ? {} : { search }),
        },
        signal,
      ),
    [page, search],
  );

  const pagination = data?.pagination ?? null;
  const items = data?.items ?? [];
  const isFirstLoad = status === 'loading' && data === null;

  return (
    <AdminShell
      title="Tenants"
      actions={
        <Link to="/tenants/new" className={buttonVariants({ size: 'sm' })}>
          <Plus aria-hidden="true" className="size-4" />
          New tenant
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-0 flex-1 sm:max-w-xs">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              className="pl-9"
              placeholder="Search name or email"
              aria-label="Search tenants"
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
            />
          </div>

          {refreshing && <Spinner size="sm" label="Updating the list" />}

          {pagination !== null && (
            <p className="ml-auto font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              {formatRange(pagination.page, pagination.pageSize, pagination.total)}
            </p>
          )}
        </div>

        <Card>
          {isFirstLoad && (
            <div className="flex items-center justify-center gap-3 px-6 py-16 text-muted">
              <Spinner size="lg" label="Loading tenants" />
            </div>
          )}

          {!isFirstLoad && status === 'error' && error !== null && (
            <ErrorState error={error} title="Could not load tenants" onRetry={reload} />
          )}

          {!isFirstLoad && status !== 'error' && items.length === 0 && (
            <div className="flex flex-col items-start gap-3 px-6 py-16">
              <p className="text-[0.95rem] text-muted">
                {search === ''
                  ? 'No workspaces have been provisioned yet.'
                  : `No workspace matches “${search}”.`}
              </p>
              {search === '' && (
                <Link to="/tenants/new" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                  Create the first one
                </Link>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[0.9rem]">
                <thead>
                  <tr className="border-b border-line">
                    <th scope="col" className={HEAD_CELL}>
                      Workspace
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Owner
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Features
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Status
                    </th>
                    <th scope="col" className={HEAD_CELL}>
                      Valid till
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((tenant) => {
                    const state = subscriptionState(tenant);

                    return (
                      <tr key={tenant.id} className="border-b border-line last:border-b-0">
                        <td className="px-4 py-3.5 align-top">
                          {/*
                            The name is the link, rather than the whole row being
                            clickable. A row-wide handler is not reachable by
                            keyboard, gives a screen reader nothing to announce,
                            and cannot be opened in a new tab — and this is the
                            only way into the screen that edits the workspace.
                          */}
                          <Link
                            to={`/tenants/${tenant.id}`}
                            className="font-medium leading-snug text-text underline-offset-4 hover:underline"
                          >
                            {tenant.name}
                          </Link>
                          <p className="font-mono text-[0.72rem] tracking-[0.04em] text-muted">
                            {tenant.email}
                          </p>
                        </td>

                        <td className="px-4 py-3.5 align-top">
                          <p className="font-mono text-[0.75rem] tracking-[0.04em] text-muted">
                            {tenant.ownerEmail ?? '—'}
                          </p>
                          <p className="text-[0.75rem] text-muted">
                            {tenant.userCount} {tenant.userCount === 1 ? 'user' : 'users'}
                          </p>
                        </td>

                        <td className="px-4 py-3.5 align-top">
                          <FeatureTags features={tenant.features} className="pt-1" />
                        </td>

                        <td className="px-4 py-3.5 align-top">
                          <Badge tone={state.tone}>{state.label}</Badge>
                        </td>

                        <td className="px-4 py-3.5 align-top">
                          <p className="whitespace-nowrap leading-snug">
                            {formatDate(tenant.validTill)}
                          </p>
                          <p className="whitespace-nowrap text-[0.75rem] text-muted">
                            {formatRelative(tenant.validTill)}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {pagination !== null && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => {
                setPage((current) => Math.max(1, current - 1));
              }}
            >
              Previous
            </Button>

            <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              Page {pagination.page} of {pagination.totalPages}
            </p>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => {
                setPage((current) => current + 1);
              }}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
